import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Role } from '@jeket-cloud/types/auth';
import { bootApprovalsApi, meApi, type PendingBootApproval } from '@/api/client';
import { useBootEvents, type BootStreamEvent } from '@/hooks/useBootEvents';
import { useFeature } from '@/hooks/useFeature';
import { RoleGate } from '@/components/auth/RoleGate';

// SSE-driven; falls back to polling only if SSE breaks for >60s. See
// PendingBoots page comments for the original rationale (#398 moved it
// here under each site).
const POLL_FALLBACK_MS = 30_000;

function ageString(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 3600_000) return `${Math.floor(ms / 60_000)}m ago`;
  return `${Math.floor(ms / 3600_000)}h ago`;
}

function truncate(s: string | null, n: number): string {
  if (!s) return '—';
  return s.length <= n ? s : s.slice(0, n - 1) + '…';
}

/**
 * Per-site Pending Boots panel (#398). Lives inside SiteDetail in
 * Deployments, alongside Nodes / Pair waiting server. Filters the
 * org-wide list down to the current site using the `siteId` field
 * that boot-service now returns on each pending row.
 *
 * Behind the `netboot` feature flag, off by default (#954): while it is off
 * for this site nothing mounts, so nothing polls boot-service or opens its
 * event stream.
 */
export default function PendingBootsPanel({ siteId }: { siteId: string }) {
  const netboot = useFeature('netboot', { siteId });
  if (!netboot) return null;
  return <PendingBootsPanelBody siteId={siteId} />;
}

function PendingBootsPanelBody({ siteId }: { siteId: string }) {
  const queryClient = useQueryClient();

  const { data: org } = useQuery({
    queryKey: ['me', 'organization'],
    queryFn: () => meApi.organization(),
    staleTime: 60_000,
  });
  const slug = org?.slug;

  const [denyingCode, setDenyingCode] = useState<string | null>(null);
  const [denyReason, setDenyReason] = useState('');
  const [busyCode, setBusyCode] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const [sseStaleFallback, setSseStaleFallback] = useState(false);
  const lastErrorAtRef = useRef<number | null>(null);

  const { data: pending, error, isLoading } = useQuery({
    queryKey: ['boot-approvals', slug],
    queryFn: () => bootApprovalsApi.listPending(slug!),
    enabled: !!slug,
    refetchInterval: sseStaleFallback ? POLL_FALLBACK_MS : false,
  });

  const onBootEvent = useCallback(
    (evt: BootStreamEvent) => {
      if (evt.kind !== 'approval_change') return;
      void queryClient.invalidateQueries({ queryKey: ['boot-approvals', slug] });
      if (evt.state === 'requested') {
        setToast({ kind: 'success', text: `New boot waiting: ${evt.code}` });
      }
    },
    [queryClient, slug],
  );

  const sse = useBootEvents({
    slug,
    enabled: !!slug,
    onEvent: onBootEvent,
  });

  useEffect(() => {
    if (sse.lastError) {
      if (lastErrorAtRef.current == null) lastErrorAtRef.current = Date.now();
      const since = Date.now() - lastErrorAtRef.current;
      if (since > 60_000) {
        setSseStaleFallback(true);
        return;
      }
      const t = setTimeout(() => setSseStaleFallback(true), 60_000 - since);
      return () => clearTimeout(t);
    }
    lastErrorAtRef.current = null;
    setSseStaleFallback(false);
  }, [sse.lastError]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ['boot-approvals', slug] });
  }

  async function onApprove(code: string) {
    if (!slug) return;
    setBusyCode(code);
    try {
      await bootApprovalsApi.approve(slug, code);
      setToast({ kind: 'success', text: `Approved ${code}` });
      await refresh();
    } catch (err) {
      setToast({ kind: 'error', text: `Approve failed: ${(err as Error).message}` });
    } finally {
      setBusyCode(null);
    }
  }

  async function onDeny() {
    if (!slug || !denyingCode || !denyReason.trim()) return;
    setBusyCode(denyingCode);
    try {
      await bootApprovalsApi.deny(slug, denyingCode, denyReason.trim());
      setToast({ kind: 'success', text: `Denied ${denyingCode}` });
      setDenyingCode(null);
      setDenyReason('');
      await refresh();
    } catch (err) {
      setToast({ kind: 'error', text: `Deny failed: ${(err as Error).message}` });
    } finally {
      setBusyCode(null);
    }
  }

  if (!slug || isLoading) return null;

  if (error) {
    return (
      <div className="bg-surface-elevated rounded-lg p-4">
        <h3 className="text-sm font-semibold text-silver-bright mb-2">Pending Boots</h3>
        <p className="text-xs text-red-400">Failed to load: {(error as Error).message}</p>
      </div>
    );
  }

  // Filter to this site's approvals. The API is still org-wide; we trim
  // client-side because the row count is small (<200).
  const all: PendingBootApproval[] = pending ?? [];
  const rows = all.filter((r) => r.siteId === siteId);

  // Hide the panel entirely when there's nothing pending — the empty state
  // would just be visual noise alongside the other site detail cards.
  if (rows.length === 0) return null;

  return (
    <div className="bg-surface-elevated rounded-lg p-4 space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-silver-bright">Pending Boots</h3>
        <p className="text-[10px] text-silver-muted mt-0.5">
          Servers waiting on approval. The 6-character code is shown on
          each rack console — approve only after confirming the machine.
        </p>
      </div>

      <div className="overflow-x-auto rounded border border-surface-deep">
        <table className="min-w-full divide-y divide-surface-deep text-xs">
          <thead className="bg-surface-deep/50">
            <tr>
              <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-silver-muted">Code</th>
              <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-silver-muted">Client IP</th>
              <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-silver-muted">User-Agent</th>
              <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-silver-muted">Age</th>
              <th className="px-3 py-2 text-right font-semibold uppercase tracking-wider text-silver-muted">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-deep">
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="px-3 py-2 font-mono text-sm text-gold">{r.code}</td>
                <td className="px-3 py-2 text-silver-bright">{r.clientIp ?? '—'}</td>
                <td className="px-3 py-2 text-silver-muted">{truncate(r.userAgent, 32)}</td>
                <td className="px-3 py-2 text-silver-muted">{ageString(r.createdAt)}</td>
                <td className="px-3 py-2 text-right space-x-1.5">
                  {/* Approving releases the site's join token to the waiting
                      box, so it is SiteOperator at the BFF and boot-service
                      (JSS#900); a Viewer would only get a 403 (JSS#907).
                      Denying is the same decision taken the other way and
                      is gated the same on all three layers (JSS#993). */}
                  <RoleGate
                    role={Role.SiteOperator}
                    fallback={
                      <span className="text-[10px] text-silver-muted">
                        A Site Operator or Admin can approve or deny
                      </span>
                    }
                  >
                    <button
                      className="rounded bg-emerald-600 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-emerald-500 disabled:opacity-50"
                      onClick={() => onApprove(r.code)}
                      disabled={busyCode === r.code}
                    >
                      Approve
                    </button>
                    <button
                      className="rounded bg-red-700 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-red-600 disabled:opacity-50"
                      onClick={() => {
                        setDenyingCode(r.code);
                        setDenyReason('');
                      }}
                      disabled={busyCode === r.code}
                    >
                      Deny
                    </button>
                  </RoleGate>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {denyingCode && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => denyingCode && !busyCode && setDenyingCode(null)}
        >
          <div className="w-full max-w-md rounded-lg bg-gray-900 p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-silver-bright">Deny boot {denyingCode}</h3>
            <p className="mt-2 text-sm text-silver-muted">
              The reason will be recorded in the audit log and shown on the rack console.
            </p>
            <textarea
              className="mt-4 w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-silver-bright"
              rows={3}
              placeholder="e.g. unexpected hardware, wrong serial number"
              value={denyReason}
              onChange={(e) => setDenyReason(e.target.value)}
              autoFocus
            />
            <div className="mt-4 flex justify-end space-x-2">
              <button
                className="rounded border border-gray-600 px-3 py-1 text-sm text-silver-bright hover:bg-gray-800"
                onClick={() => {
                  setDenyingCode(null);
                  setDenyReason('');
                }}
                disabled={!!busyCode}
              >
                Cancel
              </button>
              <button
                className="rounded bg-red-700 px-3 py-1 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50"
                onClick={onDeny}
                disabled={!denyReason.trim() || !!busyCode}
              >
                Deny
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div
          className={`fixed bottom-4 right-4 z-50 rounded px-4 py-2 text-sm text-white shadow ${
            toast.kind === 'success' ? 'bg-emerald-600' : 'bg-red-700'
          }`}
        >
          {toast.text}
        </div>
      )}
    </div>
  );
}
